import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { Waypoint } from 'react-waypoint';
import classNames from 'classnames';
import BoatReview from 'src/common/components/BoatReview';
import Modal from 'src/common/components/Modal';
import { TopReview } from 'src/common/types/api/v4/boats/search';
import { PaginatedResults } from 'src/common/types/helpers/PaginatedResults';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import { apiRequest } from 'src/common/utils/apiFetch';
import getClassNames from 'src/common/utils/getClassNames';
import { sortReviews } from './helpers';
import styles from './ReviewsList.module.scss';

const MAX_NUMBER_OF_REVIEWS = 5;

export type ReviewsListReview = Pick<TopReview, 'id' | 'user' | 'rating' | 'date_created' | 'public_review'>;

export type ReviewsListProps = {
  reviews: ReviewsListReview[];
  reviewCount: number;
  ratingAvg: number;
  listingId: string;
};

const ReviewsList: FC<ReviewsListProps> = ({
  reviews, reviewCount, ratingAvg, listingId,
}) => {
  const [t] = useTranslation('listing-page');
  const [allReviews, setAllReviews] = useState<ReviewsListReview[]>([]);
  const [nextReviewPage, setNextReviewPage] = useState<string | null>(null);
  const previewReviews = sortReviews(reviews).slice(0, MAX_NUMBER_OF_REVIEWS);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // eslint-disable-next-line max-len
  const makeRequest = useCallback(async (nextPageUrl?: string): Promise<PaginatedResults<TopReview>> => {
    setLoading(true);
    const nextPage = nextPageUrl ? new URLSearchParams(new URL(nextPageUrl).search) : 'page=1';
    const url = `/api/v4/boats/${listingId}/reviews/?${nextPage}`;
    const response = await apiRequest(url);
    setLoading(false);
    if (response.ok) {
      return response.json();
    }
    return {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };
  }, [listingId]);

  const handleResponse = (data: PaginatedResults<TopReview>) => {
    const { next, results } = data;
    setAllReviews((currentReviews) => ([
      ...currentReviews,
      ...sortReviews(results),
    ]));
    setNextReviewPage(next);
  };

  const fetchReviews = useCallback(() => {
    if (!isLoading && nextReviewPage) {
      makeRequest(nextReviewPage)
        .then(handleResponse)
        .finally(() => setLoading(false));
    }
  }, [isLoading, nextReviewPage, makeRequest]);

  useEffect(() => {
    if (isOpen && !allReviews.length) {
      makeRequest().then(handleResponse);
    }
  }, [makeRequest, allReviews.length, isOpen]);

  return reviewCount > 0 && (
    <div className={styles.root}>
      <Modal
        classNameModifier="reviewsList"
        modalOpen={isOpen}
        label="Reviews"
        closeModal={() => setIsOpen(false)}
      >
        <Modal.Header>
          {t('reviews-list--modal-header', { ratingAvg: ratingAvg.toFixed(1), reviewCount })}
        </Modal.Header>
        <Modal.Body>
          <ol className={styles.modalList}>
            {allReviews.map((review) => (
              <BoatReview
                key={review.id}
                content={review.public_review}
                date={review.date_created}
                author={review.user}
                rating={review.rating}
              />
            ))}
            <Waypoint onEnter={fetchReviews} />
            <div className={getClassNames(styles, 'loadingSpinner', classNames({ forceHeight: nextReviewPage }))}>
              {isLoading && (
                <>
                  <LoadingSpinner variant="xl" />
                  {t('reviews-list--loading-spinner')}
                </>
              )}
            </div>
          </ol>
        </Modal.Body>
      </Modal>
      <ol className={styles.list}>
        {previewReviews.map((review) => (
          <BoatReview
            key={review.id}
            content={review.public_review}
            date={review.date_created}
            author={review.user}
            rating={review.rating}
          />
        ))}
      </ol>
      {reviewCount > MAX_NUMBER_OF_REVIEWS && (
        <button
          className={styles.listModalToggle}
          type="button"
          onClick={() => {
            setIsOpen(true);
          }}
        >
          {t('reviews-list--modal-toggle', { reviewCount })}
        </button>
      )}
    </div>
  );
};

export default ReviewsList;
